import React, { useState, useEffect } from 'react'
import { Container, Card, Alert, Spinner, Button, Row, Col } from 'react-bootstrap'
import d_log from 'loglevel'
import api_utils from '../lib/api_utils'

function Favorites() {
  const [favorites, setFavorites] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchFavorites = async () => {
    setLoading(true)
    setError(null)
    try {
      d_log.debug('Fetching favorites from backend')
      const favoritesUrl = `${import.meta.env.VITE_SERVICE_URL}/infer/rag-favorites`
      const headers = api_utils.form_auth_headers()
      const response = await api_utils.call_backend_url(favoritesUrl, headers)

      // Extract favorites from the "favorites" property in the response
      const favoritesData = response?.favorites || []
      setFavorites(favoritesData)
      d_log.debug('Favorites loaded successfully:', favoritesData.length, 'items')
    } catch (err) {
      d_log.error('Failed to fetch favorites:', err)
      setError(`Failed to load favorites: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchFavorites()
  }, [])

  const handleReload = () => {
    d_log.debug('User requested reload of favorites')
    fetchFavorites()
  }

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading favorites...</span>
        </Spinner>
        <p className="mt-3">Loading favorites...</p>
      </Container>
    )
  }

  return (
    <Container>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2>Favorites</h2>
          <p className="text-muted mb-0">Your starred documents and saved conversations</p>
        </div>
        <Button
          variant="outline-primary"
          onClick={handleReload}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Reloading...
            </>
          ) : (
            <>
              🔄 Reload
            </>
          )}
        </Button>
      </div>

      {error && (
        <Alert variant="danger" className="mt-3">
          <Alert.Heading>Error Loading Favorites</Alert.Heading>
          <p>{error}</p>
        </Alert>
      )}

      <Card className="mt-4">
        <Card.Header>
          <h5>Starred Items</h5>
        </Card.Header>
        <Card.Body>
          {favorites.length === 0 ? (
            <div className="text-center py-5">
              <div className="text-muted mb-3">
                <i className="fas fa-star" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
              </div>
              <h5 className="text-muted">No Favorites Yet</h5>
              <p className="text-muted">
                Questions and answers you favorite in chat will appear here for quick access.
              </p>
            </div>
          ) : (
            <div className="mt-4">
              {favorites.map((item, index) => (
                <Card key={index} className="mb-4" style={{ border: '2px solid #ffc107' }}>
                  <Card.Header className="bg-warning bg-opacity-10">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">
                        <i className="fas fa-star text-warning me-2"></i>
                        Favorite #{index + 1}
                      </h5>
                      <small className="text-muted">
                        {item.timestamp && new Date(item.timestamp).toLocaleString()}
                      </small>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={12} className="mb-3">
                        <h6 className="text-primary mb-2">
                          <i className="fas fa-question-circle me-1"></i>
                          Question:
                        </h6>
                        <div className="p-3 bg-light rounded border-start border-primary border-3">
                          {item.question || 'No question provided'}
                        </div>
                      </Col>

                      <Col md={12}>
                        <h6 className="text-success mb-2">
                          <i className="fas fa-comment-dots me-1"></i>
                          Answer:
                        </h6>
                        <div className="p-3 bg-light rounded border-start border-success border-3">
                          {item.answer || 'No answer provided'}
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  )
}

export default Favorites